import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ToastProvider } from '@core';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { CasesState } from '../../states/cases.state';
import { ReopenCaseCommand } from './reopen-case.command';
import { ReopenCaseResult } from './reopen-case.result';

/**
 * Reopen Case Use Case
 *
 * Reopens a closed case with a mandatory reopening reason and notes.
 * The server resets the case to the "New" status, clears the assignee, and
 * re-adds it to the Intake Queue for reassignment.
 *
 * Side effects:
 * - Updates case state
 * - Shows success toast
 * - Triggers case detail reload (handled by component)
 */
@Injectable({ providedIn: 'root' })
export class ReopenCaseUseCase {
  private readonly _repository = inject(CasesRepository);
  private readonly _state = inject(CasesState);
  private readonly _toast = inject(ToastProvider);

  /**
   * Execute reopen case operation
   * @param command - Reopen case command with reason and notes
   * @returns Observable with result
   */
  public execute(command: ReopenCaseCommand): Observable<ReopenCaseResult> {
    // Set loading state
    this._state.setReopeningCase(true);
    this._state.setErrorReopeningCase(null);

    return this._repository
      .reopenCase(command.caseId, {
        reopeningReasonId: command.reopeningReasonId,
        reopeningNotes: command.reopeningNotes,
      })
      .pipe(
        tap(() => {
          // Success: update state
          this._state.setReopeningCase(false);

          // Show success toast
          this._toast.success(`Reopen request for ${command.caseNumber} submitted successfully`);
        }),
        map(() => ({
          success: true,
          caseId: command.caseId,
        })),
        catchError((error) => {
          // Error: update state
          this._state.setErrorReopeningCase(error.message || 'Failed to reopen case');
          this._state.setReopeningCase(false);

          // Show error toast
          this._toast.error(
            'Failed to reopen case',
            'Please try again or contact support if the issue persists.'
          );

          return throwError(() => error);
        })
      );
  }
}
